import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Center, Group, Loader, Text } from '@mantine/core';
import AuthCard from '@/components/auth/AuthCard';
import { userManagementService } from '@/api/services/UserManagementService';
import { getStatus } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { decodeAccessToken } from '@/auth/tokenClaims';
import { tokenStore } from '@/auth/tokenStore';
import { ROUTES } from '@/routes/paths';

// 'inactive' — цель удалена/неактивна на платформе (обмен вернул 403); 'generic' — код недействителен/устарел
type ErrorKind = 'inactive' | 'generic';

// Приём имперсонации Identix: одноразовый ?code из redirect меняем НА БЭКЕНДЕ на сессию целевого
// пользователя. Перед обменом чистим сессию оператора; code сразу убираем из URL (одноразовый, не
// должен оседать в истории), обмен не повторяем. act из токена → маркер имперсонации (для баннера).
export default function ImpersonationCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loginWithTokens, logout } = useAuth();

  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const code = searchParams.get('code');
    setSearchParams({}, { replace: true }); // одноразовый code не должен оставаться в URL/истории
    if (!code) {
      setErrorKind('generic');
      return;
    }

    logout(); // сбросить возможную сессию оператора ДО обмена (без каскада 401)

    userManagementService
      .exchangeImpersonationCode(code)
      .then((tokens) => {
        const actor = decodeAccessToken(tokens.accessToken)?.impersonator;
        if (actor) {
          tokenStore.setImpersonation(actor);
        }
        loginWithTokens(tokens);
        navigate(ROUTES.APP, { replace: true });
      })
      .catch((error) => {
        // 403 — платформа отклонила НЕактивную цель (удалён/не COMPLETED); прочее — код недействителен/устарел
        setErrorKind(getStatus(error) === 403 ? 'inactive' : 'generic');
      });
  }, [searchParams, setSearchParams, loginWithTokens, logout, navigate]);

  if (errorKind) {
    const suffix = errorKind === 'inactive' ? 'Inactive' : '';
    return (
      <AuthCard
        title={t(`auth.impersonation.errorTitle${suffix}`)}
        description={t(`auth.impersonation.errorText${suffix}`)}
      >
        <Button component={Link} to={ROUTES.LOGIN} fullWidth>
          {t('auth.impersonation.toLogin')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.impersonation.title')}>
      <Center py="md">
        <Group gap="sm">
          <Loader size="sm" />
          <Text c="gray.7">{t('auth.impersonation.loading')}</Text>
        </Group>
      </Center>
    </AuthCard>
  );
}
